/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  CheckSquare,
  ChevronDown,
  Clock,
  Library,
  Minus,
  Search,
  Square,
  X,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

type LibraryItem = {
  id: string
  name: string
  description: string
  lastUsedAt?: number
}

type LibraryPickerProps = {
  availableLibraries: LibraryItem[]
  activeLibraryIds: string[]
  onActivationChange: (ids: string[]) => void
  triggerVariant?: "default" | "footer"
  triggerLabel?: string
  showActivePills?: boolean
  popoverSide?: "top" | "bottom" | "left" | "right"
  renderInline?: boolean
  onClose?: () => void
}

type LibraryPickerContentProps = {
  filteredLibraries: LibraryItem[]
  activeLibraryIds: string[]
  toggleLibrary: (id: string) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  tab: "all" | "recent"
  setTab: (tab: "all" | "recent") => void
  setOpen: (open: boolean) => void
  activeInFilter: number
  allEnabled: boolean
  partialEnabled: boolean
  toggleMaster: () => void
  getSwitchTitle: () => string
  getSwitchBgColor: () => string
  showRecent: boolean
  isMobile?: boolean
}

function LibraryPickerContent({
  filteredLibraries,
  activeLibraryIds,
  toggleLibrary,
  searchQuery,
  setSearchQuery,
  tab,
  setTab,
  setOpen,
  activeInFilter,
  allEnabled,
  partialEnabled,
  toggleMaster,
  getSwitchTitle,
  getSwitchBgColor,
  showRecent,
  isMobile = false,
}: LibraryPickerContentProps) {
  const { t } = useTranslation()

  return (
    <>
      <div className="bg-muted/30 border-b p-3">
        <div className="mb-2 flex items-center gap-2">
          {showRecent && (
            <div className="bg-background inline-flex items-center gap-1 rounded-lg border p-1">
              <Button
                className="h-7 px-3 text-xs"
                size="sm"
                variant={tab === "all" ? "default" : "ghost"}
                onClick={() => setTab("all")}
              >
                {t("libraries.all")}
              </Button>
              <Button
                className="h-7 px-3 text-xs"
                size="sm"
                variant={tab === "recent" ? "default" : "ghost"}
                onClick={() => setTab("recent")}
              >
                {t("libraries.recent")}
              </Button>
            </div>
          )}
          <Button
            aria-label={t("common.close")}
            className="ml-auto h-7 w-7"
            size="icon"
            variant="ghost"
            onClick={() => setOpen(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 h-4 w-4" />
          <Input
            className="h-9 pl-9"
            placeholder={t("libraries.search_placeholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div
        className={cn(
          "overflow-y-auto overscroll-contain p-2",
          isMobile ? "max-h-[50vh]" : "h-[320px] max-h-[320px]"
        )}
      >
        {filteredLibraries.length === 0 ? (
          <div className="text-muted-foreground py-8 text-center text-sm">
            {t("libraries.no_results")}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredLibraries.map((library) => {
              const isActive = activeLibraryIds.includes(library.id)
              return (
                <div
                  key={library.id}
                  className={cn(
                    "rounded-lg border px-3 py-2 transition-colors",
                    isActive
                      ? "border-primary/50 bg-primary/5 ring-primary/20 ring-1"
                      : "border-border bg-background hover:bg-muted/50"
                  )}
                >
                  <div className="flex items-start gap-2">
                    <Button
                      size="icon"
                      title={isActive ? t("libraries.deactivate") : t("libraries.activate")}
                      variant="ghost"
                      className={cn(
                        "-m-0.5 size-6 shrink-0 p-0",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                      onClick={() => toggleLibrary(library.id)}
                    >
                      {isActive ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </Button>
                    {isMobile ? (
                      <button
                        aria-label={`${isActive ? t("libraries.deactivate") : t("libraries.activate")} ${library.name}`}
                        className="flex-1 text-left"
                        onClick={() => toggleLibrary(library.id)}
                      >
                        <div className="text-sm leading-tight font-medium">{library.name}</div>
                        {library.description && (
                          <div className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                            {library.description}
                          </div>
                        )}
                      </button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            aria-label={`${isActive ? t("libraries.deactivate") : t("libraries.activate")} ${library.name}`}
                            className="flex-1 text-left"
                            onClick={() => toggleLibrary(library.id)}
                          >
                            <div className="text-sm leading-tight font-medium">{library.name}</div>
                            {library.description && (
                              <div className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                                {library.description}
                              </div>
                            )}
                          </button>
                        </TooltipTrigger>
                        {library.description && library.description.length > 60 && (
                          <TooltipContent className="max-w-[300px]" side="right">
                            <p>{library.description}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="bg-background flex items-center justify-between border-t p-3">
        <span className="text-muted-foreground text-xs">
          {t("libraries.showing_count", {
            active: activeInFilter,
            total: filteredLibraries.length,
          })}
        </span>
        <div className="flex items-center gap-3">
          <Label className="text-xs" htmlFor="enable-all-switch">
            {t("libraries.enable_all")}
          </Label>
          <button
            aria-checked={partialEnabled ? "mixed" : allEnabled}
            id="enable-all-switch"
            role="switch"
            title={getSwitchTitle()}
            type="button"
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
              getSwitchBgColor()
            )}
            onClick={toggleMaster}
          >
            <span
              className={cn(
                "bg-background inline-block h-5 w-5 transform rounded-full shadow-sm transition-transform",
                allEnabled ? "translate-x-[1.375rem]" : "translate-x-0.5"
              )}
            >
              {partialEnabled && <Minus className="text-muted-foreground mx-auto my-1 h-3 w-3" />}
            </span>
          </button>
        </div>
      </div>
    </>
  )
}

export function LibraryPicker({
  availableLibraries,
  activeLibraryIds,
  onActivationChange,
  triggerVariant = "default",
  triggerLabel,
  showActivePills = true,
  popoverSide,
  renderInline = false,
  onClose,
}: LibraryPickerProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile({ breakpoint: 640 })
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [tab, setTab] = useState<"all" | "recent">("all")
  const isFooterVariant = triggerVariant === "footer"
  const resolvedLabel = triggerLabel ?? t("libraries.select_libraries")
  const resolvedPopoverSide = popoverSide ?? (isFooterVariant ? "top" : "bottom")

  const recentLibraries = useMemo(() => {
    return availableLibraries
      .filter((lib) => lib.lastUsedAt !== undefined)
      .sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0))
      .slice(0, 7)
  }, [availableLibraries])
  const hasRecentLibraries = recentLibraries.length > 0
  useEffect(() => {
    if (!hasRecentLibraries && tab === "recent") {
      setTab("all")
    }
  }, [hasRecentLibraries, tab])

  const filteredLibraries = useMemo(() => {
    let libraries = [...availableLibraries]

    if (tab === "recent") {
      libraries = libraries.filter((lib) => lib.lastUsedAt !== undefined)
      libraries.sort((a, b) => (b.lastUsedAt || 0) - (a.lastUsedAt || 0))
    } else {
      libraries.sort((a, b) => a.name.localeCompare(b.name, "sv-SE"))
    }

    const query = searchQuery.trim().toLowerCase()
    if (query) {
      libraries = libraries.filter(
        (lib) =>
          lib.name.toLowerCase().includes(query) || lib.description.toLowerCase().includes(query)
      )
    }

    return libraries
  }, [availableLibraries, tab, searchQuery])

  const toggleLibrary = (libraryId: string) => {
    const isActive = activeLibraryIds.includes(libraryId)
    if (isActive) {
      onActivationChange(activeLibraryIds.filter((id) => id !== libraryId))
    } else {
      onActivationChange([...activeLibraryIds, libraryId])
    }
  }

  const enableAll = () => {
    const filteredIds = filteredLibraries.map((lib) => lib.id)
    const newIds = Array.from(new Set([...activeLibraryIds, ...filteredIds]))
    onActivationChange(newIds)
  }

  const disableAll = () => {
    const filteredIds = new Set(filteredLibraries.map((lib) => lib.id))
    const newIds = activeLibraryIds.filter((id) => !filteredIds.has(id))
    onActivationChange(newIds)
  }

  const filteredIds = useMemo(() => filteredLibraries.map((lib) => lib.id), [filteredLibraries])
  const activeInFilter = useMemo(
    () => activeLibraryIds.filter((id) => filteredIds.includes(id)).length,
    [activeLibraryIds, filteredIds]
  )
  const allEnabled = filteredIds.length > 0 && activeInFilter === filteredIds.length
  const anyEnabled = activeInFilter > 0
  const partialEnabled = anyEnabled && !allEnabled

  const toggleMaster = () => {
    if (allEnabled) {
      disableAll()
    } else {
      enableAll()
    }
  }

  const getSwitchTitle = () => {
    if (partialEnabled) return t("common.partial")
    if (allEnabled) return t("common.enabled")
    return t("common.disabled")
  }

  const getSwitchBgColor = () => {
    if (partialEnabled) return "bg-primary/60"
    if (allEnabled) return "bg-primary"
    return "bg-muted-foreground/30"
  }

  const triggerButton = isFooterVariant ? (
    <Button
      aria-expanded={open}
      className="text-muted-foreground hover:text-foreground dark:hover:text-accent-foreground h-7 gap-1.5 px-2"
      role="combobox"
      size="md"
      type="button"
      variant="ghost"
      onMouseDown={(event) => {
        event.preventDefault()
      }}
    >
      <Library className="h-4 w-4" />
      <span>{resolvedLabel}</span>
      {activeLibraryIds.length > 0 && (
        <span className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-xs">
          {activeLibraryIds.length}
        </span>
      )}
      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
    </Button>
  ) : (
    <Button
      aria-expanded={open}
      className="justify-between text-sm"
      data-testid="library-picker-button"
      role="combobox"
      type="button"
      variant="outline"
    >
      <div className="flex items-center gap-2">
        <Library className="h-4 w-4" />
        <span>{resolvedLabel}</span>
      </div>
      <div className="flex items-center gap-2">
        {activeLibraryIds.length > 0 && (
          <span className="border-foreground bg-primary text-primary-foreground rounded-full border px-2 py-0.5 text-xs">
            {activeLibraryIds.length}
          </span>
        )}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </div>
    </Button>
  )

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      onClose?.()
    }
  }

  const contentProps = {
    filteredLibraries,
    activeLibraryIds,
    toggleLibrary,
    searchQuery,
    setSearchQuery,
    tab,
    setTab,
    setOpen: handleOpenChange,
    activeInFilter,
    allEnabled,
    partialEnabled,
    toggleMaster,
    getSwitchTitle,
    getSwitchBgColor,
    showRecent: hasRecentLibraries,
  }

  if (renderInline) {
    return (
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <LibraryPickerContent {...contentProps} isMobile={isMobile} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {isMobile ? (
        <Drawer open={open} onOpenChange={handleOpenChange}>
          <DrawerTrigger asChild>{triggerButton}</DrawerTrigger>
          <DrawerContent>
            <LibraryPickerContent {...contentProps} isMobile />
          </DrawerContent>
        </Drawer>
      ) : (
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[400px] overflow-hidden rounded-lg p-0"
            side={resolvedPopoverSide}
            sideOffset={isFooterVariant ? 8 : 0}
          >
            <LibraryPickerContent {...contentProps} />
          </PopoverContent>
        </Popover>
      )}
      {showActivePills && activeLibraryIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeLibraryIds.map((libraryId) => {
            const library = availableLibraries.find((lib) => lib.id === libraryId)
            if (!library) return null
            return (
              <button
                key={library.id}
                data-testid="library-pill"
                title={library.name}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                  "border-border bg-background text-foreground hover:bg-muted"
                )}
                onClick={() => toggleLibrary(library.id)}
              >
                <span className="max-w-[120px] truncate">{library.name}</span>
                <X
                  className="h-3 w-3 shrink-0 opacity-70 hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleLibrary(library.id)
                  }}
                />
              </button>
            )
          })}
        </div>
      )}
      {showActivePills && recentLibraries.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {recentLibraries.map((library) => {
            const isActive = activeLibraryIds.includes(library.id)
            return (
              <button
                key={library.id}
                title={library.name}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
                  isActive
                    ? "border-primary/50 bg-primary/10 text-primary hover:bg-primary/20"
                    : "border-border bg-background text-foreground hover:bg-muted"
                )}
                onClick={() => toggleLibrary(library.id)}
              >
                <Clock className="h-3 w-3 shrink-0 opacity-70" />
                <span className="max-w-[120px] truncate">{library.name}</span>
                {isActive && (
                  <X
                    className="h-3 w-3 shrink-0 opacity-70 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleLibrary(library.id)
                    }}
                  />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
